import { cosineSimilarity } from './similarity';

const activityKeyOfRequest = (request) =>
  request.id_activitate_predefinita
    ? `id:${request.id_activitate_predefinita}`
    : request.nume_activitate
      ? `name:${request.nume_activitate}`
      : null;

const featureVectorOf = (profile) => [
  ...Object.values(profile.creditsByModule || {}),
  ...Object.values(profile.creditsByCategory || {}),
];

export const userBasedScores = (dataset, profiles, studentId, neighborCount = 5) => {
  const target = profiles.get(studentId);
  if (!target) return new Map();

  const targetVector = featureVectorOf(target);
  const doneKeys = new Set(target.activityKeys || []);
  const candidates = [...profiles.values()]
    .filter((profile) => profile.studentId !== studentId)
    .map((profile) => {
      const keys = profile.activityKeys || [];
      return {
        profile,
        similarity: cosineSimilarity(targetVector, featureVectorOf(profile)),
        candidateCount: keys.filter((key) => !doneKeys.has(key)).length,
      };
    })
    .filter((entry) => entry.similarity > 0)
    .sort((a, b) => b.similarity - a.similarity);

  const withCandidates = candidates.filter((entry) => entry.candidateCount > 0);
  const neighbors = (withCandidates.length > 0 ? withCandidates : candidates).slice(0, neighborCount);

  if (neighbors.length === 0) return new Map();
  const totalSimilarity = neighbors.reduce((sum, entry) => sum + entry.similarity, 0) || 1;

  const scores = new Map();
  neighbors.forEach(({ profile, similarity }) => {
    (profile.activityKeys || []).forEach((activityKey) => {
      if (doneKeys.has(activityKey)) return;
      const current = scores.get(activityKey) || {
        activityKey,
        score: 0,
        source: 'user-cf',
        neighborCount: 0,
      };
      current.score += similarity;
      current.neighborCount += 1;
      scores.set(activityKey, current);
    });
  });

  scores.forEach((value) => {
    value.score = Math.min(1, value.score / totalSimilarity);
    if (value.neighborCount === 1) value.score *= 0.5;
  });

  return scores;
};

export const itemBasedScores = (dataset, profiles, studentId) => {
  const target = profiles.get(studentId);
  if (!target) return new Map();

  const byActivity = new Map();
  dataset.requests
    .filter(
      (request) =>
        request.status === 'validat' &&
        (!target.schoolYear || target.schoolYear === 'all' || request.an_scolar === target.schoolYear),
    )
    .forEach((request) => {
      const key = activityKeyOfRequest(request);
      if (!key) return;
      const peers = byActivity.get(key) || new Set();
      peers.add(request.id_elev);
      byActivity.set(key, peers);
    });

  const doneKeys = new Set(target.activityKeys || []);
  const scores = new Map();

  doneKeys.forEach((doneKey) => {
    const donePeers = byActivity.get(doneKey);
    if (!donePeers) return;
    byActivity.forEach((candidatePeers, candidateKey) => {
      if (doneKeys.has(candidateKey)) return;
      const shared = [...donePeers].filter((id) => candidatePeers.has(id)).length;
      if (shared < 1) return;
      const overlap = shared / Math.max(1, Math.min(donePeers.size, candidatePeers.size));
      const score = shared === 1 ? overlap * 0.5 : overlap;
      if (score <= 0) return;
      const current = scores.get(candidateKey) || {
        activityKey: candidateKey,
        score: 0,
        source: 'item-cf',
        neighborCount: 0,
        anchorKey: doneKey,
      };
      if (score > current.score) {
        current.score = score;
        current.anchorKey = doneKey;
      }
      current.neighborCount = Math.max(current.neighborCount, shared);
      scores.set(candidateKey, current);
    });
  });

  return scores;
};

export const collaborativeScores = (dataset, profiles, studentId, neighborCount = 5) => {
  const merged = new Map();
  userBasedScores(dataset, profiles, studentId, neighborCount).forEach((value, key) => merged.set(key, value));
  itemBasedScores(dataset, profiles, studentId).forEach((value, key) => {
    if (!merged.has(key)) merged.set(key, value);
  });
  return merged;
};

export const topNeighbors = (profiles, studentId, k = 5) => {
  const target = profiles.get(studentId);
  if (!target) return [];
  const targetVector = featureVectorOf(target);
  const doneKeys = new Set(target.activityKeys || []);

  return [...profiles.values()]
    .filter((profile) => profile.studentId !== studentId)
    .map((profile) => {
      const keys = profile.activityKeys || [];
      return {
        student: `${profile.nume} ${profile.prenume}`,
        similarity: Number(cosineSimilarity(targetVector, featureVectorOf(profile)).toFixed(3)),
        activityCount: keys.length,
        candidateCount: keys.filter((key) => !doneKeys.has(key)).length,
      };
    })
    .filter((entry) => entry.similarity > 0)
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, k);
};
